import React, { useState, useContext, useEffect } from 'react';
import './styles/Request.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { MdDelete } from "react-icons/md";
import { notifySuccess, ToastContainer } from '../components/ErrorAlert';
import { notifyError } from '../components/ErrorAlert';
import { NotifyMessages } from '../components/NotifyMessages';
import { IoMdArrowBack } from "react-icons/io";
import { decodeId } from "../utils/encrypt"; 
import { canEditBookingDetailsForAdmin, isAdminBookingLockedByManager } from '../utils/approvalAccess';
import { openSecureFileUrl , resolveSecureFileUrl } from "../utils/fileAccess";
import { UPDATE_BOOKING_MESSAGES } from '../constants/updateBookingMessages';
import FilePreviewModal from "../components/FilePreviewModal";

function UpdateBooking() {

  const { user, token } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams();      
  const realId = decodeId(requestId);     
  const returnTo = location.state?.returnTo || `/inbox/${requestId}`;

  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [hasSelectedFile, setHasSelectedFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [status, setStatus] = useState('');
  const [supportNeeded, setSupportNeeded] = useState('');
  const [isEditable, setIsEditable] = useState(true);
  const [managerApprovalLocked, setManagerApprovalLocked] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [formData, setFormData] = useState({
    bookingDate: '',
    bookingBy: '',
    amount: '',
    pnrNumber: '',
    fileUrl: '',
    fileName: '',
    refundAmount: "",
    lostAmount: "",
    podNumber: '',
    courierCo: '',
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {

    if (!realId) {
      setLoadError(UPDATE_BOOKING_MESSAGES.INVALID_BOOKING_LINK);
      return;
    }

    const fetchData = async () => {
      try {
        setLoadError('');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/requests/${realId}`,   
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(UPDATE_BOOKING_MESSAGES.FETCH_REQUEST_DATA_FAILED);
        }

        const requestData = await response.json();
        setManagerApprovalLocked(isAdminBookingLockedByManager(requestData));
        setIsEditable(canEditBookingDetailsForAdmin(requestData));
        setSupportNeeded(requestData.supportNeeded);
        setStatus(requestData.status);

        const data = requestData.bookingDetails;
        setExistingFiles(data.bookingFiles || []);

        setFormData({
          bookingDate: data.bookingDate ? data.bookingDate.split("T")[0] : "",
          bookingBy: data.bookingBy || "",
          amount: data.amount || "",
          pnrNumber: data.pnrNumber || "",
          fileUrl: data.fileUrl || "",
          fileName: data.fileName || "",
          refundAmount:
            data.refundAmount !== null && data.refundAmount !== undefined
              ? String(data.refundAmount)
              : "",

          lostAmount:
            data.lostAmount !== null && data.lostAmount !== undefined
              ? String(data.lostAmount)
              : "",
          podNumber: data.podNumber || "",
          courierCo: data.courierCo || "",
        });

      } catch (error) {
        console.error("Error fetching request data:", error);
        setLoadError(error.message || UPDATE_BOOKING_MESSAGES.LOAD_BOOKING_DETAILS_FAILED);
      }
    };

    fetchData();
  }, [realId, token]);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
};

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);

    const fileList = selectedFiles.map((file) => ({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
      uploaded: false,
    }));

    setFiles((prev) => [...prev, ...fileList]);
    setHasSelectedFile(true);
  };

  const handleFileDelete = (index) => {
    setFiles((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const handleExistingFileDelete = (index) => {
    const fileToDelete = existingFiles[index];

    setDeletedFiles(prev => [...prev, fileToDelete]);

    setExistingFiles(prev =>
      prev.filter((_, i) => i !== index)
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    const fileList = droppedFiles.map((file) => ({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
      uploaded: false,
    }));

    setFiles((prev) => [...prev, ...fileList]);
    setHasSelectedFile(true);
  };

  const handleViewFile = async (event, file) => {
    event.preventDefault();

    const extension = file.fileName
      .split(".")
      .pop()
      .toLowerCase();

    // DOC / DOCX -> show popup
    if (extension === "doc" || extension === "docx") {
    const previewUrl = await resolveSecureFileUrl(file.fileUrl, token);

    setPreviewFile({
      name: file.fileName,
      previewUrl,
      originalUrl: file.fileUrl,
    });

      setPreviewOpen(true);
      return;
    }

    // Everything else -> old behaviour
    try {
      await openSecureFileUrl(file.fileUrl, token);
    } catch (error) {
      console.error("Failed to open secure booking file:", error);
    }
  };
  
  const handleDownload = async () => {
    if (!previewFile) return;

    try {
      const downloadUrl = await resolveSecureFileUrl(
          previewFile.originalUrl,
          token,
          true
      );

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = previewFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!isEditable) {
    notifyError(
      managerApprovalLocked
        ? UPDATE_BOOKING_MESSAGES.MANAGER_APPROVAL_LOCKED
        : UPDATE_BOOKING_MESSAGES.BOOKING_EDIT_NOT_ALLOWED,
      "bottom-center"
    );
    return;
  }

  const isCourier = supportNeeded.includes("Courier");
  const payload = {
    ...formData,
    refundAmount:
      formData.refundAmount === "" || formData.refundAmount === null
        ? null
        : formData.refundAmount,
    lostAmount:
      formData.lostAmount === "" || formData.lostAmount === null
        ? null
        : formData.lostAmount,
    pnrNumber: isCourier ? "" : formData.pnrNumber || "",
    podNumber: isCourier ? formData.podNumber || "" : "",
    courierCo: isCourier ? formData.courierCo || "" : "",
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/requests/booking-details/${realId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload), // ✅ SEND NORMALIZED DATA
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Network response error: ${errorText}`);
    }
    for (const file of deletedFiles) {
      const deleteResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/booking-details/file/${realId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileUrl: file.fileUrl,
          }),
        }
      );

      if (!deleteResponse.ok) {
        throw new Error(UPDATE_BOOKING_MESSAGES.DELETE_FILE_FAILED);
      }
    }
    if (files.length > 0) {
      const uploadData = new FormData();

      files.forEach((fileObj) => {
        uploadData.append("files", fileObj.file);
      });
      const uploadResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/booking-details/upload/${realId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        }
      );

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(errorText);
      }
    }
    
    setFiles([]);
    setDeletedFiles([]);

    const emailResponse = await fetch(
      `${process.env.REACT_APP_API_URL}/requests/booking-details/send-email/${realId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!emailResponse.ok) {
      throw new Error(UPDATE_BOOKING_MESSAGES.SEND_BOOKING_EMAIL_FAILED);
    }

    notifySuccess(NotifyMessages.bookingSuccess, "bottom-center");
    setTimeout(() => navigate(returnTo), 1200);

  } catch (error) {
    console.error("Error submitting:", error);
    notifyError(
      String(error.message || "").includes("Booking details remain locked until any assigned manager approves the request.")
        ? UPDATE_BOOKING_MESSAGES.MANAGER_APPROVAL_LOCKED
        : 
      String(error.message || "").includes(UPDATE_BOOKING_MESSAGES.BOOKING_EDIT_NOT_ALLOWED)
        ? UPDATE_BOOKING_MESSAGES.BOOKING_EDIT_NOT_ALLOWED
        : UPDATE_BOOKING_MESSAGES.UPDATE_BOOKING_DETAILS_FAILED,
      "bottom-center"
    );
  }
};


  return (
    <div className="Request">
      <Header />
      <ToastContainer />

      <main className="main-content">
        {loadError ? (
          <div className="scroll-card">
            <div className="card-content">
              <div className="form-header">
                <div className="back-icon" onClick={() => navigate(-1)}>
                  <IoMdArrowBack />
                </div>
              </div>
              <h1>{loadError}</h1>
            </div>
          </div>
        ) : (
        <div className="scroll-card">
          <div className="card-content">

            <div className="form-header">
              <div className="back-icon" onClick={() => navigate(-1)}>
                <IoMdArrowBack />
              </div>
            </div>

            <form className="reqForm" onSubmit={handleSubmit}>
              <h1>Book a New Entry</h1>
              {managerApprovalLocked && (
                <p>Booking details are locked until any assigned manager approves this request.</p>
              )}

              <fieldset>
                <legend>Booking Details:</legend>

                <label>
                  Booking Date:
                  <input
                    type="date"
                    name="bookingDate"
                    value={formData.bookingDate}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label>
                  Booking By:
                  <input
                    type="text"
                    name="bookingBy"
                    value={formData.bookingBy}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label>
                  Amount (In Rs):
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                  />
                </label>
               <label>
               Refund Amount (In Rs):
               <input
                type="number"
                name="refundAmount"
                value={formData.refundAmount}
                onChange={handleInputChange}
               min="0"
                />
                </label>
                <label>
               Lost Amount (In Rs):
                  <input
                 type="number"
                  name="lostAmount"
                value={formData.lostAmount}
                  onChange={handleInputChange}
                 min="0"
                 />
             </label>
                {supportNeeded.includes("Courier") ? (
                  <>
                    <label>
                      Proof of Delivery (POD) Number:
                      <input
                        type="text"
                        name="podNumber"
                        value={formData.podNumber}
                        onChange={handleInputChange}
                        required
                      />
                    </label>

                    <label>
                      Courier Company:
                      <input
                        type="text"
                        name="courierCo"
                        value={formData.courierCo}
                        onChange={handleInputChange}
                        required
                      />
                    </label>
                  </>
                ) : (
                  <label>
                    PNR Number:
                    <input
                      type="text"
                      name="pnrNumber"
                      value={formData.pnrNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </label>
                )}

                <div
                    className={`upload ${isDragging ? "drag-active" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                  <label htmlFor={`fileUpload_${requestId}`} className="upload-button">
                    <strong>Booking Details:</strong> 
                    {files.length === 0 && existingFiles.length === 0 &&(
                      <FaCloudUploadAlt size={20} />
                    )}

                  <span>
                    <>
                      {existingFiles.map((file, index) => (
                        <div key={`existing-${index}`} className="view-download">
                          <a
                            href="#"
                            onClick={(e) => handleViewFile(e, file)}
                            style={{ color: "blue" }}
                          >
                            {file.fileName}
                          </a>

                          <span
                            className="delete-upload"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleExistingFileDelete(index);
                            }}
                          >
                            <MdDelete />
                          </span>
                        </div>
                      ))}
                      {files.map((file, index) => (
                        <div key={`new-${index}`} className="view-download">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();

                                const extension = file.name
                                    .split(".")
                                    .pop()
                                    .toLowerCase();

                                if (extension === "doc" || extension === "docx") {
                        setPreviewFile({
                            name: file.name,
                            previewUrl: file.url,
                            originalUrl: file.url,
                        });

                                    setPreviewOpen(true);
                                    return;
                                }

                                const link = document.createElement("a");
                        link.href = file.url;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        link.click();
                            }}
                            style={{ color: "blue" }}
                        >
                            {file.name}
                        </a>
                          <span
                            className="delete-upload"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFileDelete(index);
                            }}
                          >
                            <MdDelete />
                          </span>
                        </div>
                      ))}
                      <p className="upload-icon">
                        {isDragging
                          ? "Drop files here"
                          : files.length > 0
                            ? "Upload More"
                            : "Upload"}
                      </p>
                    </>
                  </span>

                  <input
                    type="file"
                    multiple
                    id={`fileUpload_${requestId}`}
                    name="files"
                    onChange={(e) => {
                      handleFileUpload(e);
                      e.target.value = null;    
                    }}
                    required={!hasSelectedFile && existingFiles.length === 0}
                  />
                  </label>
                </div>

                {status === "Cancelled" && (
                  <>
                    

                    <label>
                      Lost Amount (In Rs):
                      <input
                        type="number"
                        name="lostAmount"
                        value={formData.lostAmount}
                        readOnly
                      />
                    </label>
                  </>
                )}
              </fieldset>

              <button type="submit" className="submit-button" disabled={!isEditable}>
                Submit
              </button>
            </form>

          </div>
        </div>
        )}
        {/* END SCROLL WRAPPER */}

      </main>
      <FilePreviewModal
          isOpen={previewOpen}
          previewFile={previewFile}
          onClose={() => {
              setPreviewOpen(false);
              setPreviewFile(null);
          }}
          onDownload={handleDownload}
      />
      <Footer />
    </div>
  );
}

export default UpdateBooking;
